import { useMemo, useRef, useState } from "react";
import { Alert, Button, Modal, Select, Table, message } from "antd";
import { CloudUploadOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitImportPreviewDto,
  ImportMappingDto,
  ImportParseDto,
} from "../../../../domain/DataKitDto";
import { MAPPING_TARGETS, needsPairIndex, validateMapping } from "./dataKitImportMapping";
import "./DataKitImportModal.scss";

const ACCEPT = ".xlsx,.xls,.csv";
const MAX_PAIRS = 4;

interface Props {
  kit: DataKitDto;
  onClose: () => void;
  onImported: () => void;
}

export function DataKitImportModal({ kit, onClose, onImported }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [parsed, setParsed] = useState<ImportParseDto | null>(null);
  const [mapping, setMapping] = useState<ImportMappingDto[]>([]);
  const [preview, setPreview] = useState<DataKitImportPreviewDto | null>(null);
  const [busy, setBusy] = useState(false);

  const errors = useMemo(() => validateMapping(mapping), [mapping]);

  const takeFile = (next: File | null) => {
    if (!next) return;
    const name = next.name.toLowerCase();
    if (!ACCEPT.split(",").some((ext) => name.endsWith(ext))) {
      message.error("Choose a .xlsx, .xls or .csv file");
      return;
    }
    setFile(next);
  };

  const parse = () => {
    if (!file || !kit.id) return;
    setBusy(true);
    DataKitApi.parseImport(kit.id, file)
      .then((res) => {
        const data = res.data;
        setParsed(data);
        setMapping(
          data.headers.map((_, columnIndex) => {
            const suggestion = data.suggestedMapping.find((s) => s.columnIndex === columnIndex);
            const target = suggestion?.target || "IGNORE";
            return {
              columnIndex,
              target,
              pairIndex: needsPairIndex(target) ? 0 : null,
            };
          })
        );
        setStep(2);
      })
      .catch(() => message.error("Could not read that file"))
      .finally(() => setBusy(false));
  };

  const runPreview = () => {
    if (!parsed || !kit.id) return;
    setBusy(true);
    DataKitApi.previewImport(kit.id, {
      headers: parsed.headers,
      rows: parsed.rows,
      rowNumbers: parsed.rowNumbers,
      mapping: mapping.filter((m) => m.target !== "IGNORE"),
      fileName: parsed.fileName,
      resolutions: {},
    })
      .then((res) => {
        setPreview(res.data);
        setStep(3);
      })
      .catch(() => message.error("Could not preview this import"))
      .finally(() => setBusy(false));
  };

  const apply = () => {
    if (!parsed || !kit.id) return;
    setBusy(true);
    DataKitApi.applyImport(kit.id, {
      headers: parsed.headers,
      rows: parsed.rows,
      rowNumbers: parsed.rowNumbers,
      mapping: mapping.filter((m) => m.target !== "IGNORE"),
      fileName: parsed.fileName,
      resolutions: {},
    })
      .then((res) => {
        const r = res.data;
        message.success(`Imported ${r.created} new and updated ${r.updated} item${r.updated === 1 ? "" : "s"}`);
        onImported();
      })
      .catch(() => message.error("Could not finish this import"))
      .finally(() => setBusy(false));
  };

  const setTarget = (columnIndex: number, target: string) => {
    setMapping((current) =>
      current.map((m) =>
        m.columnIndex === columnIndex
          ? { ...m, target, pairIndex: needsPairIndex(target) ? m.pairIndex ?? 0 : null }
          : m
      )
    );
  };

  const setPair = (columnIndex: number, pairIndex: number) => {
    setMapping((current) =>
      current.map((m) => (m.columnIndex === columnIndex ? { ...m, pairIndex } : m))
    );
  };

  const mappingColumns = [
    {
      title: "Spreadsheet column",
      key: "header",
      render: (_: unknown, row: { columnIndex: number }) => (
        <div className="data-kit-import-col">
          <div className="data-kit-import-col-header">
            {parsed?.headers[row.columnIndex] || `Column ${row.columnIndex + 1}`}
          </div>
          <div className="data-kit-import-col-sample">
            {(parsed?.rows || [])
              .slice(0, 2)
              .map((r) => r[row.columnIndex])
              .filter((v) => !!v && v.trim() !== "")
              .join(" · ") || "—"}
          </div>
        </div>
      ),
    },
    {
      title: "Import as",
      key: "target",
      width: 220,
      render: (_: unknown, row: ImportMappingDto) => (
        <Select
          className="data-kit-import-select"
          value={row.target}
          onChange={(value) => setTarget(row.columnIndex, value)}
          options={MAPPING_TARGETS}
        />
      ),
    },
    {
      title: "Price #",
      key: "pair",
      width: 110,
      render: (_: unknown, row: ImportMappingDto) =>
        needsPairIndex(row.target) ? (
          <Select
            className="data-kit-import-pair"
            value={row.pairIndex ?? 0}
            onChange={(value) => setPair(row.columnIndex, value)}
            options={Array.from({ length: MAX_PAIRS }, (_, i) => ({
              label: String(i + 1),
              value: i,
            }))}
          />
        ) : null,
    },
  ];

  const footer =
    step === 1
      ? [
          <Button key="cancel" onClick={onClose} disabled={busy}>
            Cancel
          </Button>,
          <Button key="next" type="primary" onClick={parse} loading={busy} disabled={!file}>
            Next
          </Button>,
        ]
      : step === 2
      ? [
          <Button key="back" onClick={() => setStep(1)} disabled={busy}>
            Back
          </Button>,
          <Button
            key="next"
            type="primary"
            onClick={runPreview}
            loading={busy}
            disabled={errors.length > 0}
          >
            Next
          </Button>,
        ]
      : [
          <Button key="back" onClick={() => setStep(2)} disabled={busy}>
            Back
          </Button>,
          <Button key="apply" type="primary" onClick={apply} loading={busy}>
            Import
          </Button>,
        ];

  return (
    <Modal
      className="data-kit-import-modal clean one"
      open
      width={step === 1 ? 600 : 760}
      onCancel={onClose}
      footer={footer}
      title={
        <div className="data-kit-import-steps">
          {[1, 2, 3].map((n) => (
            <span key={n} className="data-kit-import-step-wrap">
              <span className={`data-kit-import-step${step === n ? " active" : ""}${step > n ? " done" : ""}`}>
                {n}
              </span>
              {n < 3 && <span className="data-kit-import-step-line" />}
            </span>
          ))}
        </div>
      }
    >
      {step === 1 && (
        <div className="data-kit-import-upload">
          <h3>Import from spreadsheet</h3>
          <p className="data-kit-import-copy">
            Upload a CSV or Excel file.
            <br />
            We'll read your columns and help you map them to menu fields.
          </p>

          <div
            className={`data-kit-import-dropzone${dragging ? " dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              takeFile(e.dataTransfer.files?.[0] || null);
            }}
          >
            <CloudUploadOutlined className="data-kit-import-dropicon" />
            {file ? (
              <p className="data-kit-import-filename">{file.name}</p>
            ) : (
              <p className="data-kit-import-droptext">
                Drag and drop your file here
                <br />
                or{" "}
                <button type="button" onClick={() => fileInput.current?.click()}>
                  select file from your computer
                </button>
              </p>
            )}
            <p className="data-kit-import-formats">.xlsx / .xls / .csv</p>
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => takeFile(e.target.files?.[0] || null)}
            />
          </div>
        </div>
      )}

      {step === 2 && parsed && (
        <div className="data-kit-import-mapping">
          <h3>Map your columns</h3>
          <p className="data-kit-import-copy">
            {parsed.fileName} — {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"}.
            Item name and Group are required.
          </p>

          {errors.length > 0 && (
            <Alert
              className="data-kit-import-errors"
              type="warning"
              showIcon
              message={errors.map((e) => (
                <div key={e}>{e}</div>
              ))}
            />
          )}

          <Table
            className="data-kit-import-table"
            dataSource={mapping}
            columns={mappingColumns}
            rowKey="columnIndex"
            pagination={false}
            size="small"
          />
        </div>
      )}

      {step === 3 && preview && (
        <div className="data-kit-import-preview">
          <h3>Review this import</h3>
          <div className="data-kit-import-summary">
            <span className="data-kit-import-stat">
              <strong>{preview.creates.length}</strong> new
            </span>
            <span className="data-kit-import-stat">
              <strong>{preview.updates.length}</strong> updated
            </span>
            {preview.ambiguous.length > 0 && (
              <span className="data-kit-import-stat warn">
                <strong>{preview.ambiguous.length}</strong> ambiguous
              </span>
            )}
            {preview.skipped.length > 0 && (
              <span className="data-kit-import-stat warn">
                <strong>{preview.skipped.length}</strong> skipped
              </span>
            )}
          </div>

          {preview.ambiguous.length > 0 && (
            <Alert
              className="data-kit-import-errors"
              type="warning"
              showIcon
              message={`${preview.ambiguous.length} row${preview.ambiguous.length === 1 ? "" : "s"} match more than one existing item and will be skipped.`}
            />
          )}

          <Table
            className="data-kit-import-table"
            dataSource={[...preview.creates, ...preview.updates]}
            rowKey="rowNumber"
            pagination={{ pageSize: 8, showSizeChanger: false }}
            size="small"
            columns={[
              {
                title: "Row",
                dataIndex: "rowNumber",
                key: "rowNumber",
                width: 70,
              },
              { title: "Item", dataIndex: "name", key: "name" },
              { title: "Group", dataIndex: "groupName", key: "groupName", width: 160 },
              {
                title: "Price",
                key: "price",
                width: 140,
                render: (_: unknown, row: { prices?: { size: string; price: string }[] | null }) =>
                  (row.prices || []).map((p) => p.price).join(" / ") || "––",
              },
              {
                title: "",
                key: "kind",
                width: 90,
                render: (_: unknown, row: { matchedItemId?: string | null }) => (
                  <span className={`data-kit-import-kind${row.matchedItemId ? " update" : ""}`}>
                    {row.matchedItemId ? "Update" : "New"}
                  </span>
                ),
              },
            ]}
          />

          {preview.skipped.length > 0 && (
            <div className="data-kit-import-skipped">
              {preview.skipped.slice(0, 5).map((s) => (
                <div key={s.rowNumber}>
                  Row {s.rowNumber}: {s.reason}
                </div>
              ))}
              {preview.skipped.length > 5 && (
                <div>and {preview.skipped.length - 5} more…</div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
